import { usePlanFeatures } from "../hooks/usePlanFeatures";
import PaywallCard from "./PaywallCard";
import { PageHeader } from "./layout/PageHeader";
import {
  Loader2,
  Package,
  UtensilsCrossed,
  Truck,
  Sparkles,
  ClipboardList,
  Plug,
  Share2,
  ShoppingCart,
  Inbox,
  Wallet,
  ChefHat,
  Calculator,
} from "lucide-react";

/**
 * Per-module metadata for the unified header that renders above the paywall.
 * Keyed by `moduleKey`; falls back to no header if missing.
 */
const MODULE_META = {
  recipes:        { icon: UtensilsCrossed, section: "Carta y cocina" },
  inventory:      { icon: Package,         section: "Operaciones" },
  suppliers:      { icon: Truck,           section: "Operaciones" },
  forecast:       { icon: Sparkles,        section: "Finanzas y análisis" },
  shopping_lists: { icon: ClipboardList,   section: "Operaciones" },
  tpv:            { icon: Plug,            section: "Integraciones" },
  social:         { icon: Share2,          section: "Presencia digital" },
  finance:        { icon: Calculator,      section: "Finanzas y análisis" },
};

/**
 * PlanGate — wraps a route so users whose active plan does not include the
 * required module see a PaywallCard with an upsell instead of the module UI.
 *
 * When paywalled, also renders a PageHeader above the paywall so the gated
 * page keeps the same visual rhythm (breadcrumb + icon + title + description)
 * as accessible pages.
 */
export default function PlanGate({
  moduleKey,
  label,
  description,
  benefits,
  requiredPlan = "Essentials",
  children,
}) {
  const { hasModule, loading } = usePlanFeatures();

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid={`plan-gate-loading-${moduleKey}`}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasModule(moduleKey)) {
    const meta = MODULE_META[moduleKey];
    const Icon = meta?.icon;
    return (
      <div className="space-y-6" data-testid={`plan-gate-paywall-${moduleKey}`}>
        {meta && (
          <PageHeader
            icon={Icon ? <Icon className="w-5 h-5" /> : null}
            title={label}
            description={description}
            breadcrumb={[{ label: meta.section }, { label }]}
          />
        )}
        <div className="px-4 pb-10 sm:px-0">
          <PaywallCard
            moduleLabel={label}
            description={description}
            benefits={benefits}
            requiredPlan={requiredPlan}
          />
        </div>
      </div>
    );
  }

  return children;
}
